<template>
  <div class="validate-phone-wrapper">
    <div class="tips">{{ t('lang269') }}</div>
    <div class="form-item phone-input">
      <div class="area-code">+{{ phoneInfo.prefix }}</div>
      <div class="phone-num">{{ phoneInfo.phone }}</div>
    </div>

    <div class="form-item code-send">
      <input
        class="code-input"
        v-model="verifyCode"
        maxlength="10"
        type="phone"
        :placeholder="t('lang268')"
      />
      <button class="send-btn" type="button" :disabled="timer.running" @click="onSend">
        {{ timer.running ? t('lang024', [timer.current]) : t('lang023') }}
      </button>
    </div>
    <div class="footer">
      <a-button type="primary" :disabled="!verifyCode" @click="onOk">{{ t('lang244') }}</a-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import useCountdown from '@/compositions/useCountdown';
import { useAppStore } from '@/store/app';
import {
  useLiveAgentUnionOssSendVerifyCode,
  useLiveAgentUnionOssCheckVerifyCode,
  LiveAgentUnionOssCheckVerifyCodeRequest,
  LiveAgentUnionOssSendVerifyCodeRequest,
} from '@/api/yapi-req';
import { COMMON_ERROR_TEXT, SEND_VERIFY_CODE_SCENE, SEND_VERIFY_CODE_TYPE } from '@/constants';
import { STATUS_CODE } from './constants';
import { Message } from '@arco-design/web-vue';
import { useThrottleFn } from '@vueuse/core';
import useTrack from '@/compositions/useTrack';

const { t } = useI18n();
const SEND_CODE_STORAGE_KEY = 'CHECK_PHONE_CODE_LAST_SEND_TIME';
const SEND_CODE_COUNTDOWN = 60;
const timer = useCountdown(SEND_CODE_STORAGE_KEY, SEND_CODE_COUNTDOWN);
const { report } = useTrack('check_phone');

const { run: sendVerifyCode, loading: sending } = useLiveAgentUnionOssSendVerifyCode({
  manual: true,
  onSuccess: () => {
    timer.start();
    report('sendVerifyCodeSuccess');
  },
  onError: () => {
    Message.error(t(COMMON_ERROR_TEXT));
    report('sendVerifyCodeError');
    timer.reset();
  },
});
const { run: checkVerifyCode, loading: checking } = useLiveAgentUnionOssCheckVerifyCode({
  manual: true,
  onSuccess: () => {
    appStore.setPhoneAuthSuccess();
    report('checkPhoneSuccess');
    close();
  },
  onError: (res) => {
    report('checkPhoneError');
    if (res.code === STATUS_CODE.CHECK_CODE_FAILED) {
      Message.error(t('lang330'));
      return;
    }
    Message.error(t(COMMON_ERROR_TEXT));
  },
});

const appStore = useAppStore();

const verifyCode = ref('');

const emit = defineEmits(['click']);

const phoneInfo = computed(() => {
  return appStore.phoneInfo;
});

const onSend = useThrottleFn(() => {
  if (timer.running || sending.value) {
    return;
  }
  sendVerifyCode({
    type: SEND_VERIFY_CODE_TYPE.PHONE_MSG,
    scene: SEND_VERIFY_CODE_SCENE.CHECK_AUTH,
  } as unknown as LiveAgentUnionOssSendVerifyCodeRequest);
}, 300);

const onOk = useThrottleFn(() => {
  if (checking.value) {
    return;
  }
  checkVerifyCode({
    type: SEND_VERIFY_CODE_TYPE.PHONE_MSG,
    scene: SEND_VERIFY_CODE_SCENE.CHECK_AUTH,
    code: verifyCode.value,
  } as unknown as LiveAgentUnionOssCheckVerifyCodeRequest);
}, 300);

const close = () => emit('click');

onMounted(() => {
  report('displayCheckPhoneModal');
});
</script>

<style lang="less" scoped>
.validate-phone-wrapper {
  box-sizing: border-box;

  .tips {
    margin-bottom: 20px;
    color: var(--color-text-1, #1d2129);

    /* 14/CN-Regular */
    font-family: PingFang SC;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px; /* 157.143% */
  }

  .form-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 7px 16px;
    height: 48px;
    border-radius: 8px;
    background: var(--color-fill-1, #f7f8fa);
    padding: 7px 16px;

    /* 14/CN-Regular */
    font-family: PingFang SC;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;

    .phone-num {
      color: var(--color-text-1, #1d2129);
      font-family: PingFang SC;
      font-weight: 700;
    }

    input {
      line-height: 22px;
      border: none;
      background: transparent;
      flex: 1;

      color: var(--color-text-1, #1d2129);
      font-family: PingFang SC;
      font-weight: 700;

      &:focus {
        border: none;
        background: transparent;
        outline: none;
      }
      &::placeholder {
        color: var(--color-text-3, #86909c);
        font-weight: 400;
      }
    }

    .label {
      color: var(--color-text-3, #86909c);
    }
  }
  .area-select {
    color: var(--color-text-1, #1d2129);

    /* 14/CN-Regular */
    font-family: PingFang SC;

    .cur-area {
      cursor: pointer;
      display: flex;
      align-items: center;
      height: 100%;
    }
  }

  .phone-input {
    justify-content: flex-start;
    .area-code {
      flex-shrink: 0;
      margin-right: 8px;

      color: var(--color-text-1, #1d2129);

      /* 14/EN-Bold */
      font-family: PingFang SC;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 22px; /* 157.143% */
    }
  }

  .code-send {
    position: relative;
    .code-input {
      flex: 1;
      max-width: 60%;
    }
  }
  .send-btn {
    min-width: 60px;
    padding: 0 3px;
    font-weight: 700;
    color: var(--color-text-1);
    background: none;
    border: none;
    cursor: pointer;
    height: 100%;
    &[disabled] {
      color: var(--color-text-3, #86909c);
    }
  }

  .footer {
    text-align: center;
    padding: 32px;
  }
}
</style>
